/**
 * ═══════════════════════════════════════════════════════════════════════════
 * KART GİRDİSİ DÖNÜŞTÜRÜCÜLERİ — `bind` içinde `{ param, map }` (Faz 8 dalga 2)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Kart eylemi kullanıcı girdisini operasyon parametresine YALNIZ `bind` ile
 * yazıyor (`./actions.ts`). Çoğu girdi parametreye olduğu gibi gidiyor (metin
 * → ad, seçim listesi → kelime dizisi). Reklam TAKVİMİ öyle değil: kart tek
 * bir biçim konuşuyor — 7×24 gün-saat ızgarası (`grid`, satır 0 = Pazartesi,
 * hesabın saat diliminde) — ama üç platform üç ayrı biçim istiyor:
 *
 *   Meta    `meta.adset.set_schedule.dayparting` — dakika aralıkları + gün
 *           listesi (0 = PAZAR), en fazla 50 dilim; hepsi açık = `null`
 *           (gün bölmeyi kaldır).
 *   Google  `google.targeting.set_schedule.slots` — gün başına `HH:MM`
 *           aralıkları, gün başına en fazla 6; hepsi açık = `[]` (tüm gün).
 *   TikTok  `tiktok.adgroup.update.dayparting` — 336 karakterlik 0/1 dizesi
 *           (7 gün × 48 yarım saat, Pazartesi 00:00'dan).
 *
 * Çeviriyi istemciye bırakmak, istemcinin parametre YAZMASI demekti (kart
 * sözleşmesinin kapattığı kapı). Burada ADLA seçilen, SAF, sınırlı bir
 * dönüştürücü: eylem tanımı yalnız adını taşır (kart JSON'a yazılıyor,
 * fonksiyon yazılamaz), sunucu tıklamada çağırır (`action-policy.ts`).
 * Geçersiz girdi (hiç açık saat yok, gün başına çok dilim) FIRLATIR → uç
 * `CARD_ACTION_INVALID_INPUT`.
 *
 * Yeni dönüştürücü = buraya bir satır + testi. Genel amaçlı bir ifade dili
 * DEĞİL ve olmamalı.
 */

/** Izgara: 7 satır (Pzt…Paz) × 24 sütun (00…23), 0/1. */
export type CardScheduleGrid = readonly (readonly (0 | 1)[])[];

export const CARD_INPUT_MAP_NAMES = [
  'grid.meta_dayparting',
  'grid.google_schedule',
  'grid.tiktok_dayparting',
] as const;
export type CardInputMapName = (typeof CARD_INPUT_MAP_NAMES)[number];

export class CardInputMapError extends Error {}

/** Girdiyi adlı dönüştürücüden geçirir — tanınmayan ad ya da biçim FIRLATIR. */
export function applyCardInputMap(name: string, raw: unknown): unknown {
  switch (name) {
    case 'grid.meta_dayparting':
      return metaDayparting(asGrid(raw));
    case 'grid.google_schedule':
      return googleSchedule(asGrid(raw));
    case 'grid.tiktok_dayparting':
      return tiktokDayparting(asGrid(raw));
    default:
      throw new CardInputMapError(`Unknown input map: ${name}`);
  }
}

function asGrid(raw: unknown): CardScheduleGrid {
  if (
    !Array.isArray(raw) ||
    raw.length !== 7 ||
    raw.some((row) => !Array.isArray(row) || row.length !== 24 || row.some((cell) => cell !== 0 && cell !== 1))
  ) {
    throw new CardInputMapError('The schedule must be a 7×24 grid of 0/1.');
  }
  const grid = raw as CardScheduleGrid;
  if (!grid.some((row) => row.includes(1))) {
    throw new CardInputMapError('At least one hour must stay on — an empty schedule would stop delivery entirely.');
  }
  return grid;
}

function allOn(grid: CardScheduleGrid): boolean {
  return grid.every((row) => row.every((cell) => cell === 1));
}

/** Bir günün açık saat aralıkları — `[başlangıç, bitiş)` saat. */
export function scheduleDayRuns(row: readonly (0 | 1)[]): { start: number; end: number }[] {
  const runs: { start: number; end: number }[] = [];
  let start: number | null = null;
  for (let hour = 0; hour <= 24; hour += 1) {
    const on = hour < 24 && row[hour] === 1;
    if (on && start === null) start = hour;
    if (!on && start !== null) {
      runs.push({ start, end: hour });
      start = null;
    }
  }
  return runs;
}

/**
 * Meta — aynı saat aralığını paylaşan günler TEK dilimde (dilim tavanı 50).
 * Gün numarası Meta'nın: 0 = Pazar … 6 = Cumartesi; ızgara satırı 0 =
 * Pazartesi. Saat dilimi `ADVERTISER`: kart ızgarayı hesabın saatinde
 * çiziyor, izleyicinin saatiyle (`USER`) uygulamak başka saatler demek olurdu.
 */
function metaDayparting(grid: CardScheduleGrid): unknown {
  if (allOn(grid)) return null;
  const byRun = new Map<string, { start: number; end: number; days: number[] }>();
  grid.forEach((row, index) => {
    for (const run of scheduleDayRuns(row)) {
      const key = `${run.start}-${run.end}`;
      const entry = byRun.get(key) ?? { ...run, days: [] };
      entry.days.push((index + 1) % 7);
      byRun.set(key, entry);
    }
  });
  const slots = [...byRun.values()].map((run) => ({
    startMinute: run.start * 60,
    endMinute: run.end * 60,
    days: [...run.days].sort((a, b) => a - b),
    timezone: 'ADVERTISER' as const,
  }));
  if (slots.length > 50) {
    throw new CardInputMapError('Meta allows at most 50 schedule blocks — merge some hours.');
  }
  return slots;
}

const GOOGLE_DAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'] as const;

function hhmm(hour: number): string {
  return `${String(hour).padStart(2, '0')}:00`;
}

/** Google — gün başına en fazla 6 aralık; hepsi açık = boş liste (tüm gün). */
function googleSchedule(grid: CardScheduleGrid): unknown {
  if (allOn(grid)) return [];
  const slots: { day: string; from: string; to: string }[] = [];
  grid.forEach((row, index) => {
    const runs = scheduleDayRuns(row);
    if (runs.length > 6) {
      throw new CardInputMapError(`Google allows at most 6 time blocks per day (${GOOGLE_DAYS[index]}).`);
    }
    for (const run of runs) slots.push({ day: GOOGLE_DAYS[index]!, from: hhmm(run.start), to: hhmm(run.end) });
  });
  return slots;
}

/** TikTok — 7 × 48 yarım saat; her açık saat iki yarım saat. */
function tiktokDayparting(grid: CardScheduleGrid): unknown {
  return grid.map((row) => row.map((cell) => (cell === 1 ? '11' : '00')).join('')).join('');
}
